import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonIcon from "@mui/icons-material/Person";
import Parse from "parse";
import { useNotify } from "react-admin";

export const ManagementTree = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHierarchy, setSelectedHierarchy] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [upwardHierarchy, setUpwardHierarchy] = useState([]);
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [loadedNodes, setLoadedNodes] = useState(new Set());

  const fetchManagementTree = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🌳 Fetching management tree...");

      const result = await Parse.Cloud.run("getManagementTree");

      if (result.success) {
        setTreeData(result.treeData);
        setUpwardHierarchy(result.upwardHierarchy || []);
        setCurrentUserRole(result.currentUser.role);
        setCurrentUserId(result.currentUser.id);
        
        // Auto-expand root node
        setExpandedNodes(new Set([result.treeData.id]));

        console.log("✅ Management tree loaded successfully");
      } else {
        throw new Error("Failed to load management tree");
      }
    } catch (err) {
      console.error("❌ Error fetching management tree:", err);
      setError(err.message || "Failed to load management tree");
      notify(err.message || "Failed to load management tree", { type: "error" });
    } finally {
      setLoading(false);
    }
  };
  // Fetch management tree on mount
  useEffect(() => {
    fetchManagementTree();
  }, []);


  // Date validation function
  const validateDates = (start, end) => {
    if (!start && !end) {
      setDateError("");
      return true;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (start) {
      const startDateObj = new Date(start);
      if (startDateObj > today) {
        setDateError("Start date cannot be in the future");
        return false;
      }
    }

    if (end) {
      const endDateObj = new Date(end);
      if (endDateObj > today) {
        setDateError("End date cannot be in the future");
        return false;
      }
    }

    if (start && end) {
      const startDateObj = new Date(start);
      const endDateObj = new Date(end);
      if (endDateObj < startDateObj) {
        setDateError("End date must be after start date");
        return false;
      }
    }

    setDateError("");
    return true;
  };

  // Handle start date change with validation
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    validateDates(newStartDate, endDate);
  };

  // Handle end date change with validation
  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    validateDates(startDate, newEndDate);
  };

  const fetchUserStatistics = async (userId) => {
    try {
      // Validate dates before fetching
      if (!validateDates(startDate, endDate)) {
        notify(dateError || "Invalid date range", { type: "error" });
        return;
      }

      console.log(`📊 Fetching statistics for user: ${userId}`);

      const params = { userId };
      
      // Add date filters if provided
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const result = await Parse.Cloud.run("getUserStatistics", params);

      if (result.success) {
        const userData = {
          username: result.user.username,
          role: result.user.role,
          balance: result.user.balance,
          totalPlayed: result.statistics.totalPlayed,
          totalWin: result.statistics.totalWin,
          totalRecharge: result.statistics.totalRecharge,
          totalRedeem: result.statistics.totalRedeem,
          totalIncome: result.statistics.totalIncome,
          holding: result.statistics.holding,
        };

        setSelectedUser(userData);
        console.log("✅ User statistics loaded successfully");
      } else {
        throw new Error("Failed to load user statistics");
      }
    } catch (err) {
      console.error("❌ Error fetching user statistics:", err);
      notify(err.message || "Failed to load user statistics", { type: "error" });
    }
  };



  const fetchNodeChildren = async (nodeId) => {
    try {
      setLoadingNodes((prev) => new Set(prev).add(nodeId));
      
      console.log(`📦 Fetching children for node: ${nodeId}`);
      
      const result = await Parse.Cloud.run("getNodeChildren", { userId: nodeId });
      
      if (result.success) {
        // Update tree data with fetched children
        const updateNodeChildren = (node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              children: result.children,
            };
          }
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: node.children.map(updateNodeChildren),
            };
          }
          return node;
        };
        
        setTreeData((prevTree) => updateNodeChildren(prevTree));
        setLoadedNodes((prev) => new Set(prev).add(nodeId));
        
        console.log(`✅ Children loaded for node: ${nodeId}`);
      }
    } catch (err) {
      console.error("❌ Error fetching node children:", err);
      notify(err.message || "Failed to load children", { type: "error" });
    } finally {
      setLoadingNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }
  };

  const toggleNode = async (nodeId) => {
    const isExpanded = expandedNodes.has(nodeId);
    
    if (isExpanded) {
      // Collapse node
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    } else {
      // Expand node
      setExpandedNodes((prev) => new Set(prev).add(nodeId));
      
      // Fetch children if not already loaded
      if (!loadedNodes.has(nodeId)) {
        await fetchNodeChildren(nodeId);
      }
    }
  };

  const expandAll = async () => {
    const allIds = new Set();
    const nodesToLoad = [];
    
    const collectIds = (node) => {
      if (!node) return;
      allIds.add(node.id);
      
      // Check if node has children that need to be loaded
      if (node.hasChildren && !loadedNodes.has(node.id)) {
        nodesToLoad.push(node.id);
      }
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(collectIds);
      }
    };
    
    if (treeData) collectIds(treeData);
    setExpandedNodes(allIds);
    
    // Load all nodes that haven't been loaded yet
    for (const nodeId of nodesToLoad) {
      await fetchNodeChildren(nodeId);
    }
  };

  const collapseAll = () => {
    setExpandedNodes(new Set([treeData?.id]));
  };

  const filterTree = (node, term) => {
    if (!node) return null;
    if (!term) return node;

    const matchesSearch =
      node.username?.toLowerCase().includes(term.toLowerCase()) ||
      node.email?.toLowerCase().includes(term.toLowerCase()) ||
      node.role?.toLowerCase().includes(term.toLowerCase());

    const filteredChildren = node.children
      ?.map((child) => filterTree(child, term))
      .filter(Boolean);

    if (matchesSearch || (filteredChildren && filteredChildren.length > 0)) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    return null;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "Super-User":
        return "#0288d1";
      case "Game-Owner":
        return "#0288d1";
      case "Master-Distributor":
        return "#ab47bc";
      case "Distributor":
        return "#d32f2f";
      case "Sub-Distributor":
        return "#f57c00";
      case "Agent":
        return "#737373";
      case "Player":
        return "#388e3c";
      default:
        return "#737373";
    }
  };

  const buildHierarchyPath = (targetNode, currentNode = treeData, path = []) => {
    if (!currentNode) return null;

    const newPath = [...path, { username: currentNode.username, role: currentNode.role }];

    if (currentNode.id === targetNode.id) {
      return newPath;
    }

    if (currentNode.children) {
      for (const child of currentNode.children) {
        const result = buildHierarchyPath(targetNode, child, newPath);
        if (result) return result;
      }
    }

    return null;
  };

  const handleNodeClick = (node) => {
    // Don't fetch stats for virtual nodes
    if (node.isVirtual) {
      console.log("Skipping virtual node");
      return;
    }

    const hierarchy = buildHierarchyPath(node);
    setSelectedHierarchy(hierarchy || []);
    
    // Fetch real statistics from backend
    if (node.id) {
      fetchUserStatistics(node.id);
    }
  };

  const renderTreeNode = (node, level = 0) => {
    if (!node) return null;

    const hasChildren = node.hasChildren || (node.children && node.children.length > 0);
    const isExpanded = expandedNodes.has(node.id);
    const isLoadingChildren = loadingNodes.has(node.id);

    return (
      <Box key={node.id} sx={{ ml: level * 3, mb: 1 }}>
        <Box
          onClick={() => handleNodeClick(node)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid rgba(255, 239, 153, 0.20)",
            bgcolor: "#1A1A1A",
            transition: "all 0.2s ease",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#2B2B2B",
              borderColor: "rgba(255, 193, 7, 0.4)",
            },
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id);
              }}
              disabled={isLoadingChildren}
              sx={{
                p: 0.5,
                color: "rgba(255, 255, 255, 0.7)",
                "&:hover": {
                  color: "#FFC107",
                  bgcolor: "rgba(255, 193, 7, 0.08)",
                },
              }}
            >
              {isLoadingChildren ? (
                <CircularProgress size={20} sx={{ color: "#FFC107" }} />
              ) : isExpanded ? (
                <ExpandMoreIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 32 }} />
          )}

          <PersonIcon sx={{ color: "#FFC107" }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, color: "#fff" }}
            >
              {node.username}
            </Typography>
            {node.email && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.6)" }}
              >
                {node.email}
              </Typography>
            )}
          </Box>

          <Chip
            label={node.role}
            size="small"
            variant="outlined"
            sx={{
              mr: 1,
              borderColor: getRoleColor(node.role),
              color: getRoleColor(node.role),
              fontWeight: 500,
            }}
          />

          {!node.isVirtual && (
            <Tooltip title="Balance">
              <Chip
                label={`${node.balance?.toLocaleString() || 0}`}
                size="small"
                variant="outlined"
                sx={{
                  mr: 1,
                  borderColor: "#388e3c",
                  color: "#4caf50",
                  fontWeight: 500,
                }}
              />
            </Tooltip>
          )}

          {hasChildren && (
            <Chip
              label={`${node.childrenCount || node.children?.length || 0} subordinate${
                (node.childrenCount || node.children?.length || 0) !== 1 ? "s" : ""
              }`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: "rgba(255, 255, 255, 0.3)",
                color: "rgba(255, 255, 255, 0.7)",
              }}
            />
          )}
        </Box>

        {hasChildren && isExpanded && (
          <Box sx={{ mt: 1 }}>
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const displayTree = searchTerm && treeData ? filterTree(treeData, searchTerm) : treeData;

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setDateError("");
    setSelectedUser(null);
  };

  const handleTodayFilter = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setDateError("");
  };

  // Handle search button click - refetch selected user stats with date filters
  const handleSearch = () => {
    if (!validateDates(startDate, endDate)) {
      notify(dateError || "Invalid date range", { type: "error" });
      return;
    }
    
    // If a user is selected, refetch their statistics with the new date filters
    if (selectedUser) {
      // Find the selected user's ID from the tree
      const findUserIdByUsername = (node, username) => {
        if (!node) return null;
        if (node.username === username) return node.id;
        if (node.children) {
          for (const child of node.children) {
            const found = findUserIdByUsername(child, username);
            if (found) return found;
          }
        }
        return null;
      };
      
      const userId = findUserIdByUsername(treeData, selectedUser.username);
      if (userId) {
        fetchUserStatistics(userId);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ backgroundColor: "#242424", borderRadius: 2 }}>
        <CardContent>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mb={3}
            flexWrap="wrap"
          >
            <TextField
              variant="outlined"
              placeholder="Search by username or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 250,
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2B2B2B",
                  color: "#fff",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FFC107",
                  },
                },
              }}
            />
            
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{
                max: new Date().toISOString().split("T")[0],
              }}
              error={!!dateError && dateError.toLowerCase().includes("start")}
              helperText={dateError && dateError.toLowerCase().includes("start") ? dateError : ""}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2B2B2B",
                  color: "#fff",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiFormHelperText-root": {
                  color: "#f44336",
                },
              }}
            />

            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{
                max: new Date().toISOString().split("T")[0],
                min: startDate || undefined,
              }}
              error={!!dateError && dateError.toLowerCase().includes("end")}
              helperText={dateError && dateError.toLowerCase().includes("end") ? dateError : ""}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2B2B2B",
                  color: "#fff",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiFormHelperText-root": {
                  color: "#f44336",
                },
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!!dateError}
              sx={{
                background: "#FFC107",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": { backgroundColor: "#FFA000" },
                "&:disabled": {
                  backgroundColor: "rgba(255, 193, 7, 0.3)",
                  color: "rgba(0, 0, 0, 0.5)",
                },
              }}
            >
              Apply
            </Button>

            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{
                borderColor: "#FFC107",
                color: "#FFC107",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#FFA000",
                  backgroundColor: "rgba(255, 193, 7, 0.08)",
                },
              }}
            >
              Reset Filters
            </Button>

            <Button
              variant="outlined"
              onClick={handleTodayFilter}
              sx={{
                borderColor: "#FFC107",
                color: "#FFC107",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#FFA000",
                  backgroundColor: "rgba(255, 193, 7, 0.08)",
                },
              }}
            >
              Today
            </Button>

            <Button
              variant="contained"
              onClick={expandAll}
              sx={{
                background: "#FFC107",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": { backgroundColor: "#FFA000" },
              }}
            >
              Expand All
            </Button>

            <Button
              variant="outlined"
              onClick={collapseAll}
              sx={{
                borderColor: "#FFC107",
                color: "#FFC107",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#FFA000",
                  backgroundColor: "rgba(255, 193, 7, 0.08)",
                },
              }}
            >
              Collapse All
            </Button>
          </Box>

          {upwardHierarchy.length > 0 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: "#1A1A1A",
                border: "1px solid rgba(255, 193, 7, 0.3)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#FFC107", mb: 1, fontWeight: 600 }}
              >
                Your Upward Hierarchy:
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                {upwardHierarchy.map((item, index) => (
                  <React.Fragment key={index}>
                    <Chip
                      label={`${item.username} (${item.role})`}
                      size="small"
                      sx={{
                        bgcolor: "#2B2B2B",
                        color: "rgba(255, 255, 255, 0.7)",
                        borderColor: getRoleColor(item.role),
                        border: "1px solid",
                        opacity: 0.8,
                      }}
                    />
                    {index < upwardHierarchy.length - 1 && (
                      <ChevronRightIcon sx={{ color: "rgba(255, 193, 7, 0.5)" }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
             
            </Box>
          )}

          {selectedUser && (
            <TableContainer
              component={Paper}
              sx={{
                mb: 3,
                backgroundColor: "#242424",
                borderRadius: 1,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Username
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Role
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Balance
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Total Played
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Total Win
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Total Recharge
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Total Redeem
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Total Income
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                        backgroundColor: "#242424",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      Holding %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.username}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      <Chip
                        label={selectedUser.role}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: getRoleColor(selectedUser.role),
                          color: getRoleColor(selectedUser.role),
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#4caf50",
                        fontWeight: 500,
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.balance.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalPlayed.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalWin.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalRecharge.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalRedeem.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalIncome.toLocaleString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
                      }}
                    >
                      {selectedUser.totalRecharge > 0
                        ? (
                            (selectedUser.totalIncome /
                              selectedUser.totalRecharge) *
                            100
                          ).toFixed(2)
                        : "0"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {selectedHierarchy.length > 0 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: "#1A1A1A",
                border: "1px solid rgba(255, 193, 7, 0.4)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#FFC107", mb: 1, fontWeight: 600 }}
              >
                Selected Hierarchy Path:
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                {selectedHierarchy.map((item, index) => (
                  <React.Fragment key={index}>
                    <Chip
                      label={`${item.username} (${item.role})`}
                      size="small"
                      sx={{
                        bgcolor: "#2B2B2B",
                        color: "#fff",
                        borderColor: getRoleColor(item.role),
                        border: "1px solid",
                      }}
                    />
                    {index < selectedHierarchy.length - 1 && (
                      <ChevronRightIcon sx={{ color: "#FFC107" }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          <Box>
            {loading ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
                gap={2}
              >
                <CircularProgress sx={{ color: "#FFC107" }} />
                <Typography sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                  Loading
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : displayTree ? (
              renderTreeNode(displayTree)
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                align="center"
              >
                No results found
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
